import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Text } from 'app/design/primitives/text';
import { isMobileNative } from 'app/utils/web-helpers';
import { useClickOutside } from 'app/hooks/use-click-outside';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconCalendar, IconEye, IconEyeOff, IconPencil, IconTrash } from '@tabler/icons-react-native';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { ListingCardHorizontalMenuItem } from './listing-card-horizontal-menu-item';

const theme = createDSTheme();

export function ListingCardHorizontalMenu({
  isOpen,
  setIsMenuOpen,
  onBlockDates,
  onChangeLoanFee,
  onHide,
  onUnhide,
  onDelete,
  style,
}: {
  isOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  onBlockDates?: () => void;
  onChangeLoanFee?: () => void;
  onHide?: () => void;
  onUnhide?: () => void;
  onDelete?: () => void;
  style?: StyleProp<ViewStyle>;
}) {
  const menuRef = useRef(null);
  const { t } = useTranslation('my-listings');
  useClickOutside([menuRef], (clickedOutside) => {
    if (clickedOutside) setIsMenuOpen(false);
  });
  const actions = [
    { label: t('blockDatesButton'), onPress: onBlockDates, icon: <IconCalendar strokeWidth={ICON_STROKE_WIDTH} /> },
    {
      label: t('changeLoanFeeButton'),
      onPress: onChangeLoanFee,
      icon: <IconPencil strokeWidth={ICON_STROKE_WIDTH} size={30} />,
    },
    { label: t('hideButton'), onPress: onHide, icon: <IconEyeOff strokeWidth={ICON_STROKE_WIDTH} /> },
    { label: t('unhideButton'), onPress: onUnhide, icon: <IconEye strokeWidth={ICON_STROKE_WIDTH} /> },
    {
      label: t('deleteButton'),
      onPress: onDelete,
      icon: <IconTrash strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.error']} />,
      textColor: theme.colors['text.error'],
    },
  ].filter((action) => action.onPress);

  // Native devices have no document-level outside-click listener. The shared sheet
  // provides backdrop, swipe and Android back dismissal without choosing an action.
  if (isMobileNative) {
    return (
      <BottomSheet open={isOpen} onClose={() => setIsMenuOpen(false)}>
        {actions.map((action) => (
          <BottomSheet.Action
            key={action.label}
            prefix={action.icon}
            onPress={() => {
              setIsMenuOpen(false);
              action.onPress?.();
            }}
          >
            <Text style={{ color: action.textColor }}>{action.label}</Text>
          </BottomSheet.Action>
        ))}
      </BottomSheet>
    );
  }

  if (!isOpen) return null;

  return (
    <View style={[styles.menu, style]} ref={menuRef}>
      {actions.map((action) => (
        <ListingCardHorizontalMenuItem
          key={action.label}
          icon={action.icon}
          label={action.label}
          textColor={action.textColor}
          onPress={() => {
            action.onPress?.();
            setIsMenuOpen(false);
          }}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  menu: {
    position: 'absolute',
    top: 46,
    width: 200,
    right: 0,
    backgroundColor: 'white',
    borderRadius: 8,
    paddingVertical: 4,
    rowGap: theme.spacing['spacing.general.x1'],
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.15)',
  },
});
